import { IconSend } from '@tabler/icons-react'
import { useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router'

import { CanWrite } from '../components/CanWrite'
import { useSession } from '../auth/useSession'
import { Alert, Badge, EmptyState, ErrorState, FilteredEmptyState, LoadingState } from '../components/States'
import type { DeliveryStatus, DeliverySummary } from '../lib/apiTypes'
import { formatCount, formatDateTimeWib } from '../lib/format'
import { useClients, useDeliveries, useRedeliver } from '../lib/hooks'
import { deliveryStatusLabel, deliveryStatusTone } from '../lib/status'

const STATUSES: DeliveryStatus[] = ['DEAD', 'PENDING', 'DELIVERED']

/**
 * Daftar delivery webhook. Bawaannya status DEAD, karena itulah yang perlu
 * ditindaklanjuti operator: seluruh percobaan ulang sudah habis.
 */
export function WebhooksPage() {
  const { user } = useSession()
  const [params, setParams] = useSearchParams()
  const status = (params.get('status') ?? 'DEAD') as DeliveryStatus
  const client = params.get('client') ?? ''
  const [cursor, setCursor] = useState<string | undefined>(undefined)
  const [extra, setExtra] = useState<DeliverySummary[]>([])
  const [feedback, setFeedback] = useState<{ tone: 'success' | 'danger'; text: string } | null>(
    null,
  )

  const query = useMemo(() => ({ status, client: client || undefined, cursor }), [status, client, cursor])
  const deliveries = useDeliveries(query)
  const clients = useClients()
  const redeliver = useRedeliver()

  const rows = cursor ? mergeRows(extra, deliveries.data?.data ?? []) : (deliveries.data?.data ?? [])

  function setStatus(next: DeliveryStatus) {
    const params2 = new URLSearchParams(params)
    params2.set('status', next)
    setParams(params2, { replace: true })
    setCursor(undefined)
    setExtra([])
  }

  function setClient(next: string) {
    const params2 = new URLSearchParams(params)
    if (next) {
      params2.set('client', next)
    } else {
      params2.delete('client')
    }
    setParams(params2, { replace: true })
    setCursor(undefined)
    setExtra([])
  }

  return (
    <>
      <div className="page-head">
        <div className="page-head__text">
          <h1>Webhook</h1>
          <p className="muted">
            Setiap perubahan status menjadwalkan satu webhook ke client. Delivery yang
            gagal dikirim ulang dengan jeda yang makin panjang, lalu berhenti dengan status
            gagal kirim.
          </p>
        </div>
      </div>

      {!user?.merchant_scope_restricted && clients.isError ? <ErrorState title="Gagal memuat pilihan client" error={clients.error} onRetry={() => void clients.refetch()} /> : null}
      <div className="card">
        <div className="chip-row" role="group" aria-label="Filter status delivery">
          {STATUSES.map((item) => (
            <button
              key={item}
              type="button"
              className="chip"
              aria-pressed={status === item}
              onClick={() => setStatus(item)}
            >
              {deliveryStatusLabel(item)}
            </button>
          ))}
        </div>

        <div className="toolbar">
          <div className="field">
            <label className="field__label" htmlFor="wh-client">
              Client
            </label>
            <select
              id="wh-client"
              disabled={Boolean(user?.merchant_scope_restricted) || clients.isPending || clients.isError}
              className="select"
              value={client}
              onChange={(event) => setClient(event.target.value)}
            >
              <option value="">{user?.merchant_scope_restricted ? 'Client dalam cakupan merchant' : clients.isPending ? 'Memuat client...' : 'Semua client'}</option>
              {(clients.data?.data ?? []).map((item) => (
                <option key={item.id} value={item.slug}>
                  {item.name} ({item.slug})
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {feedback ? <Alert tone={feedback.tone}>{feedback.text}</Alert> : null}

      {deliveries.isPending ? (
        <LoadingState what="daftar delivery webhook" />
      ) : deliveries.isError ? (
        <ErrorState
          title="Gagal memuat daftar webhook"
          error={deliveries.error}
          onRetry={() => void deliveries.refetch()}
          retrying={deliveries.isFetching}
        />
      ) : rows.length === 0 ? (
        client ? (
          <FilteredEmptyState what="delivery webhook" onClear={() => setClient('')} />
        ) : status === 'DEAD' ? (
          <EmptyState
            title="Tidak ada webhook yang gagal kirim"
            body="Semua delivery berhasil terkirim. Delivery yang gagal setelah seluruh percobaan ulang akan muncul di sini."
            action={
              <button type="button" className="btn" onClick={() => setStatus('PENDING')}>
                Lihat yang masih menunggu kirim
              </button>
            }
          />
        ) : (
          <EmptyState
            title={`Tidak ada delivery berstatus ${deliveryStatusLabel(status).toLowerCase()}`}
            body="Belum ada delivery pada status ini. Coba status lain untuk melihat antrean yang berbeda."
          />
        )
      ) : (
        <section aria-labelledby="delivery-heading">
          <div className="page-head">
            <div className="page-head__text">
              <h2 id="delivery-heading">{formatCount(rows.length)} delivery ditampilkan</h2>
            </div>
          </div>
          <div className="rows">
            {rows.map((row) => (
              <article className="row-item" key={row.id}>
                <div className="row-item__main">
                  <span className="row-item__title">{row.event_type}</span>
                  <span className="row-item__meta">
                    Client {row.client}, percobaan {row.attempts}, dibuat{' '}
                    {formatDateTimeWib(row.created_at)}
                  </span>
                  <span className="row-item__meta">
                    {row.last_status_code
                      ? `Balasan terakhir HTTP ${row.last_status_code}`
                      : 'Belum ada balasan HTTP'}
                    {row.last_error ? `, ${row.last_error}` : ''}
                    {row.delivered_at
                      ? `, terkirim ${formatDateTimeWib(row.delivered_at)}`
                      : `, berikutnya ${formatDateTimeWib(row.next_attempt_at)}`}
                  </span>
                  <Link className="table__link" to={`/transactions/${row.payment_id}`}>
                    Buka transaksi terkait
                  </Link>
                </div>
                <div className="state__actions">
                  <Badge tone={deliveryStatusTone(row.status)}>
                    {deliveryStatusLabel(row.status)}
                  </Badge>
                  <CanWrite>
                    <button
                      type="button"
                      className="btn btn--sm"
                      disabled={redeliver.isPending}
                      onClick={() => {
                        setFeedback(null)
                        redeliver.mutate(row.id, {
                          onSuccess: () =>
                            setFeedback({
                              tone: 'success',
                              text: 'Webhook dijadwalkan kirim ulang. Perubahan statusnya terlihat setelah pengiriman selesai.',
                            }),
                          onError: () =>
                            setFeedback({
                              tone: 'danger',
                              text: 'Webhook tidak bisa dijadwalkan kirim ulang. Coba lagi sebentar lagi.',
                            }),
                        })
                      }}
                    >
                      <IconSend size={16} stroke={1.75} aria-hidden="true" />
                      Kirim ulang
                    </button>
                  </CanWrite>
                </div>
              </article>
            ))}
          </div>
          <div className="state__actions">
            {deliveries.data?.next_cursor ? (
              <button
                type="button"
                className="btn"
                disabled={deliveries.isFetching}
                onClick={() => {
                  setExtra(rows)
                  setCursor(deliveries.data?.next_cursor)
                }}
              >
                {deliveries.isFetching ? 'Memuat...' : 'Muat berikutnya'}
              </button>
            ) : (
              <p className="muted">Semua delivery yang cocok sudah ditampilkan.</p>
            )}
          </div>
        </section>
      )}
    </>
  )
}

function mergeRows(previous: DeliverySummary[], incoming: DeliverySummary[]): DeliverySummary[] {
  const seen = new Set(previous.map((row) => row.id))
  return [...previous, ...incoming.filter((row) => !seen.has(row.id))]
}
